import React, { useEffect, useState, useRef, memo, useCallback, useContext } from "react";
import { Row, Col, Empty, Button, Image } from "antd";
import "./TwoDImages.scss";
import { Gallery } from "react-grid-gallery";
import { ReactComponent as ImgOutline } from '../../../../assets/images/black-large-img-outline.svg'
import DottedLoader from "../../DottedLoader";
import {
  QA_IMG_URI,
  LOW_RES_QA_IMG_URI, AI_GENERATED_GLB, DEFAULT_BASE_URL
} from "../../../../environments/env";
import { getDisplayEngineName, getEngineLogo } from "../../Utilities";
// import "@google/model-viewer";
import { FullscreenOutlined, EditOutlined, CaretRightFilled } from "@ant-design/icons";
import TwoDImagesContext from "../../ContextFiles/TwoDImagesContext";
import VideoCard from "./VideoCard";
import playIconSvg from "../../../../assets/images/play-circle-icon.svg";
let isAiCustomer = localStorage.getItem('is_ai_customer') === 'true';

const ExploreThumbnail = memo(({ item, imageProps, onVideoClick, onImageClick }) => {
  const isVideo = item.image_type === 'ai_video';
  const { onClick, ...restImageProps } = imageProps;
  
  return (
    <div
      style={{ 
        position: "relative", 
        minHeight: "100px", 
        minWidth: "100px",
        width: "100%",
        height: "100%",
        cursor: 'pointer'
      }}
      onClick={() => {
        if (isVideo && onVideoClick) {
          onVideoClick(item.videoUrl || item.src);
        }
      }}
    >
      <Image
        className={item.image_type === 'ai' || item.image_type === 'ai_video' ? 'ai-image' : ''}
        {...restImageProps}
        src={item.src}
        preview={isVideo ? false : {
          src: item.videoUrl || item.src,
          onVisibleChange: (visible) => {
            if (visible && onImageClick) {
              onImageClick(item);
            }
          }
        }}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'block'
        }}
      />
      {isVideo && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 2,
          pointerEvents: 'none'
        }}>
          <img 
            src={playIconSvg} 
            alt="Play" 
            style={{ width: '60px', height: '60px' }} 
          />
        </div>
      )}
    </div>
  );
});

const InfiniteScrollGallery = memo(({ images, loading, onImageClick, onAIImageClick, onVideoClick, type, hasAnyFinalImages, useAntdPreview, paginationState, searchValue }) => {
  const [displayedImages, setDisplayedImages] = useState([]);
  const [remainingCredits, setRemainingCredits] = useState(0);
  const pageRef = useRef(1);
  const imagesLengthRef = useRef(0);
  const pageSize = 20;
  const galleryRef = useRef();
  
  // Get stack state management from context (with fallbacks for when context is not available)
  const context = useContext(TwoDImagesContext);
  const {
    enterStackState = () => {},
    stackState = false,
    stackImages = [],
    setImageType = () => {},
    selectionMode = false,
    selectedImageIds = new Set(),
    toggleSelectImage = () => {},
    setFilteredImageIds = () => {},
    setFilteredImagesRef = () => {},
  } = context || {};




  const loadMoreImages = useCallback(() => {
    if (!images || images.length === 0) return;
    
    const startIndex = (pageRef.current - 1) * pageSize;
    const newImages = images.slice(startIndex, startIndex + pageSize);

    if (newImages.length > 0) {
      const newDisplayedImages = newImages.map((image) => {
        let processedImage = { ...image };
        if (!processedImage.videoUrl) {
            processedImage.videoUrl = image.src || image.url;
        }

        if (image.image_type == "mp4") {
          processedImage.src = image.mp4Preview;
        }
        else if(image.image_type == "lifestyle" || image.image_type == "silo" || image.image_type == "ai" || image.image_type == "ai_video") {
          // in case of lifestyle of silo image, change image src to low res thumbnail
          // so that thumbnail is displayed in grid gallery preview
          processedImage.src = image.thumbnail;
        }

        // Normalize dimensions to ensure consistent aspect ratio for grid layout
        if (type === 'ai_explore') {
          if (image.image_height > image.image_width) {
            processedImage.width = 9;
            processedImage.height = 13;
          } else {
            processedImage.width = image.image_width || 1;
            processedImage.height = image.image_height || 1;
          }
        } else if (type === 'ai_video') {
          processedImage.width = 1;
          processedImage.height = 1;
        }
        
        return processedImage;
      });
      setDisplayedImages((prev) => [...prev, ...newDisplayedImages]);
      pageRef.current += 1;
    } else {
      console.log("No more images to load.");
    }
  }, [images, type, pageSize]);

  // Track first image ID to detect dataset changes vs appends
  const prevFirstImageIdRef = useRef(null);

  useEffect(() => {
    if (!images || images.length === 0) {
      setDisplayedImages([]);
      pageRef.current = 1;
      prevFirstImageIdRef.current = null;
      return;
    }
    
    // To Avoid UI Flicker:
    // 1. Reset if the dataset is completely different (first image ID changed)
    // 2. Reset if the dataset shrunk (likely a search/filter being applied)
    // 3. If dataset grew and first image is the same, it's an append (no reset needed)
    const firstImg = images[0];
    const currentFirstId = firstImg.image_type === 'ai' || firstImg.image_type === 'ai_video'
      ? `${firstImg.image_type}_${firstImg.id}`
      : `${firstImg.image_type}_${firstImg.entity_id}_${firstImg.image_name || firstImg.filename || firstImg.camera_name}`;

    const isFirstIdDifferent = prevFirstImageIdRef.current !== currentFirstId;
    const isDatasetShrunk = images.length < (imagesLengthRef.current || 0);

    if (isFirstIdDifferent || isDatasetShrunk) {
      prevFirstImageIdRef.current = currentFirstId;
      imagesLengthRef.current = images.length;
      setDisplayedImages([]);
      pageRef.current = 1;
      loadMoreImages();
    } else if (images.length > (imagesLengthRef.current || 0)) {
      // Just update length ref to allow loading more from the new source
      imagesLengthRef.current = images.length;
    }
  }, [images, loadMoreImages]);


  const handleScroll = useCallback(() => {
    if (!galleryRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = galleryRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      loadMoreImages();
    }
  }, [loadMoreImages]);

  // Stable image key for selection tracking
  const getImageKey = useCallback((img) => {
    return (img.image_type === 'ai' || img.image_type === 'ai_video')
      ? `${img.image_type}_${img.id}`
      : `${img.image_type}_${img.entity_id}_${img.image_name || img.display_name || img.uri || img.id}`;
  }, []);

  // Sync filtered image IDs to parent context so SelectAll works.
  // Use a ref to track the previous image list length + first ID to avoid
  // unnecessary state updates that would cause re-render loops.
  const prevSyncKeyRef = useRef(null);
  useEffect(() => {
    if (!images || images.length === 0) return;
    const syncKey = `${images.length}_${getImageKey(images[0])}`;
    if (syncKey === prevSyncKeyRef.current) return;
    prevSyncKeyRef.current = syncKey;
    setFilteredImageIds(images.map(getImageKey));
    setFilteredImagesRef(images);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images]);

  // Auto-fill effect for large screens:
  // After images are displayed, check if the gallery viewport is filled.
  // If not, and there's more to load, trigger another load.
  useEffect(() => {
    // Small timeout to allow Gallery layout to settle
    const timer = setTimeout(() => {
      if (galleryRef.current && displayedImages.length > 0) {
        const { scrollHeight, clientHeight } = galleryRef.current;
        const totalAvailable = (images && images.length) || 0;
        
        // If scrollable height is not greater than visible height (no scrollbar),
        // and we haven't exhausted the source images list, load the next batch.
        // We use a small buffer (10px) to be sure.
        if (scrollHeight <= clientHeight + 10 && displayedImages.length < totalAvailable) {
          loadMoreImages();
        }
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [displayedImages.length, images?.length, loadMoreImages]);

  const handleAIImageClick = (imageData) => {
    const aiImageData = {
      ...imageData,
      selectedImageUrl: imageData.src.replace(LOW_RES_QA_IMG_URI, QA_IMG_URI).replace("_512.", "."),
      renderIndex: 0, // For single images, use index 0
      renders: [imageData.src.replace(LOW_RES_QA_IMG_URI, QA_IMG_URI).replace("_512.", ".")], // Single render array
      prompt_text: imageData.prompt_text || imageData.display_name,
      product_id: imageData.entity_id || null,
      id: imageData.id || imageData.render_id || null
    };
    
    if (onAIImageClick) {
      onAIImageClick(aiImageData);
    }
  };

  const getEngineClass = (engine) => {
    if (!engine) return '';
    const lowerEngine = engine.toString().toLowerCase();
    if (lowerEngine.includes('gemini')) return 'engine-gemini';
    if (lowerEngine.includes('flux')) return 'engine-flux';
    if (lowerEngine.includes('qwen')) return 'engine-qwen';
    if (lowerEngine.includes('claude')) return 'engine-claude';
    if (lowerEngine.includes('gpt')) return 'engine-gpt';
    if (lowerEngine.includes('gpt-image-1.5')) return 'engine-gpt';
    return `engine-${lowerEngine.replace(/[^a-z0-9]/g, '-')}`;
  };

  const CustomThumbnailWithLoader = ({ item, imageProps }) => {
    if (item.image_type === "ai_generated_glb") {
      return (
        <div style={{ position: "relative", minHeight: "100px", minWidth: "100px" }}>
          <FullscreenOutlined className={`carousel-ico fs product-card`}
          onClick={() => onImageClick(item.src)} 
          />
          <model-viewer
            src={(type == "ai_explore" ? "https://d48f7equ64qjl.cloudfront.net/" : DEFAULT_BASE_URL) + item.uri}
            alt={item.caption || "3D Model"}
            auto-rotate
            camera-controls
            ar
            style={{ width: "100%", height: "300px" }}
          />
        </div>
      );
    }
    
    // Handle stacked AI images - only show stack styling when not in stack state and in AI/All tabs
    if (item.image_type === 'ai' && item.isStackDisplay && !stackState && (type === 'ai' || type === 'all')) {
      const engineVal = item.engine;
      const engineDisplayName = getDisplayEngineName(engineVal);
      const engineLogo = getEngineLogo(engineVal);
      // const engineClass = getEngineClass(engineVal); // Removed engine class from stacked card container
      // console.log('Stack Image Engine:', engineVal, 'Class:', engineClass);
      
      return (
        <div 
          className={`stacked-card stacked-card-top-right ai-stacked-image`}
          style={{ marginTop: 0 }}
        >
          <div className="box" style={{ borderRadius: '3px', position: 'relative'}}> 
            <img
              className="ai-image"
              {...imageProps}
              style={{
                ...imageProps.style,
              }}
            />
            {/* Stack count indicator with EditOutlined icon */}
            {item.stackCount >= 1 && (
              <div className="edit-icon-image-stack">
                <EditOutlined style={{ fontSize: '12px', color: 'black' }} />
                <span>{item.stackCount}</span>
              </div>
            )}
            </div>
          </div>

      );
    }

    // AI video now uses the default image flow but with a play icon

    // You can access the original props passed to the image component via imageProps
    const engineVal = item.engine;
    const engineClass = (item.image_type === 'ai') ? getEngineClass(engineVal) : '';
    // const engineDisplayName = getDisplayEngineName(engineVal);
    // const engineLogo = getEngineLogo(engineVal);
    // console.log('Single Image Engine:', engineVal, 'Class:', engineClass, 'Image Type:', item.image_type);
    
    const isInProgress = item.status === 'in-progress' || item.status === 'inprogress';
    const imageKey = getImageKey(item);
    const isSelected = selectionMode && selectedImageIds.has(imageKey);

    return (
      <div
        className={engineClass}
        style={{
          position: "relative",
          minHeight: "100px",
          minWidth: "100px",
          width: "100%",
          height: "100%",
          cursor: isInProgress ? "not-allowed" : 'pointer',
          pointerEvents: isInProgress ? "none" : 'auto',
          outline: isSelected ? '3px solid #1890ff' : 'none',
          outlineOffset: '-3px',
        }}
      >
        <img
          className={(item.image_type === 'ai' || item.image_type === 'ai_video') ? 'ai-image' : ''}
          {...imageProps}
          style={{
            ...imageProps.style,
            filter: isInProgress ? "blur(2px) brightness(.7)" : (isSelected ? 'brightness(0.85)' : ''),
          }}
        />
        {/* Selection checkbox overlay */}
        {selectionMode && (
          <div
            style={{
              position: 'absolute',
              top: '8px',
              left: '8px',
              zIndex: 10,
              pointerEvents: 'auto',
              width: '22px',
              height: '22px',
              borderRadius: '4px',
              border: isSelected ? 'none' : '2px solid rgba(255,255,255,0.9)',
              background: isSelected ? '#1890ff' : 'rgba(0,0,0,0.25)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
              transition: 'all 0.15s ease',
            }}
            onClick={(e) => {
              e.stopPropagation();
              toggleSelectImage(imageKey);
            }}
          >
            {isSelected && (
              <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                <path d="M1 5L4.5 8.5L11 1.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </div>
        )}
        {item.image_type === 'ai_video' && !isInProgress && null}
      </div>
    );
  };

  // Show loading indicator with count if pagination is active
  if (loading && paginationState && paginationState.loaded > 0) {
    return (
      <div>
        <div
          ref={galleryRef}
          className="comment-input-box__suggestions"
          style={{ overflowY: "auto", height: `${window.innerHeight * 0.8}px` }}
        >
          <Gallery
            onClick={(index) => {
              const imageData = displayedImages[index];
              const { index: clickedIndex, src, image_type, status, is_final_image } = imageData;

              if (selectionMode) {
                toggleSelectImage(getImageKey(imageData));
                return;
              }

              if (status === 'in-progress' || status === 'inprogress') {
                return;
              }

              if (imageData.isStackDisplay && imageData.stackImages && !stackState && (type === 'ai' || type === 'all')) {
                enterStackState(imageData.stackImages);
              }
              else if ((image_type === "ai" || image_type === "ai_video") && !is_final_image) {
                handleAIImageClick(imageData);
              } else if (image_type !== "ai_generated_glb") {
                onImageClick(src.replace(LOW_RES_QA_IMG_URI, QA_IMG_URI).replace("_512.", ".")); 
              }
            }}
            rowHeight={300}
            margin={(type === 'ai' || type === 'final_images') ? 8 : 8}
            images={displayedImages}
            enableImageSelection={false}
            thumbnailImageComponent={CustomThumbnailWithLoader}
            tagStyle={{
              display: 'inline-flex',
              alignItems: 'center',
              background: 'transparent',
              color: '#333',
              padding: '0',
              margin: '0',
              cursor: 'default',
              gap: '8px',
              fontWeight: '600'
            }}
          />
        </div>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          padding: '20px',
          gap: '8px'
        }}>
          <DottedLoader custom_class_ai="ai-section" />
          <span className="manrope f-14 w-500 black-085">
            {paginationState.loaded}+ images loaded
          </span>
        </div>
      </div>
    );
  }

  if (loading) return   <div style={{ overflowY: "auto", height: `calc(100vh - 400px)`, width: `calc(100vw - 500px)` }}>
  <DottedLoader custom_class_ai="ai-section" />
</div>;

  if (!displayedImages.length && !(type === "final_images" && hasAnyFinalImages === false)) {
    if (type === 'ai_explore') {
        return (
          <Row justify="center" align="middle" style={{ marginTop: "5%" }}>
            <Col span={24} style={{ textAlign: "center" }}>
              <ImgOutline />
              <span className="manrope f-20 mt-24" style={{ display: "block" }}>
                {searchValue?.trim() ? "No images found matching your search" : "No images to display"}
              </span>
            </Col>
          </Row>
        );
    }
    return (
      <Row>
        <Col span={24}>
          <Empty description="No data available" />
        </Col>
      </Row>
    );
  }
  if (type === "final_images" && hasAnyFinalImages === false)
  {
    return (
      <Row justify="center" align="middle" style={{ marginTop: "5%" }}>
        <Col span={24} style={{ textAlign: "center" }}>
          <ImgOutline />
          <span className="manrope f-20 mt-24" style={{ display: "block" }}>You haven't saved any final images yet</span>
          {isAiCustomer && <span className="manrope f-16 mt-8" style={{ display: "block" }}>You have {remainingCredits} remaining credits</span>}
          <div className="mt-16" style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
            <Button 
              onClick={() => setImageType('ai')}
              className="browse-previews-btn"
            >
              Browse Generations
            </Button>
            
            {isAiCustomer && <Button
              type="primary"
              className="buy-credits-btn"
            >
              Buy More Credits
            </Button>}
          </div>
        </Col>
      </Row>
    );
  }
  
  // If using Ant Design Image Preview for ai_explore
  if (useAntdPreview && type === 'ai_explore') {
    return (
      <div>
        <div
          ref={galleryRef}
          onScroll={handleScroll}
          className="comment-input-box__suggestions"
          style={{ overflowY: "auto", height: `${window.innerHeight * 0.8}px` }}
        >
          <Gallery
            rowHeight={300}
            margin={8}
            images={displayedImages}
            enableImageSelection={false}
            thumbnailImageComponent={(props) => (
              <ExploreThumbnail 
                {...props} 
                onVideoClick={onVideoClick} 
                onImageClick={onImageClick}
              />
            )}
            tagStyle={{
              display: 'inline-flex',
              alignItems: 'center',
              background: 'transparent',
              color: '#333',
              padding: '0',
              margin: '0',
              cursor: 'default',
              gap: '8px',
              fontWeight: '600'
            }}
          />
        </div>
      </div>
    );
  }
  
  return (
    <div>
      <div
        ref={galleryRef}
        onScroll={handleScroll}
        className="comment-input-box__suggestions"
        style={{ overflowY: "auto", height: `${window.innerHeight * 0.8}px` }}
      >
        <Gallery
          onClick={(index) => {
            const imageData = displayedImages[index];
            const { index: clickedIndex, src, image_type, status, is_final_image } = imageData;

            // In selection mode, toggle selection instead of opening viewer
            if (selectionMode) {
              toggleSelectImage(getImageKey(imageData));
              return;
            }

            // Prevent click action if status is in-progress
            if (status === 'in-progress' || status === 'inprogress') {
              return;
            }

            if (imageData.isStackDisplay && imageData.stackImages && !stackState && (type === 'ai' || type === 'all')) {
              enterStackState(imageData.stackImages);
            }
            else if ((image_type === "ai" || image_type === "ai_video") && !is_final_image) {
              handleAIImageClick(imageData);
            } else if (image_type !== "ai_generated_glb") {
              onImageClick(src.replace(LOW_RES_QA_IMG_URI, QA_IMG_URI).replace("_512.", "."));
            }
          }}
          rowHeight={300}
          margin={(type === 'ai' || type === 'final_images') ? 8 : 8}
          images={displayedImages}
          enableImageSelection={false}
          thumbnailImageComponent={CustomThumbnailWithLoader}
          tagStyle={{
            display: 'inline-flex',
            alignItems: 'center',
            background: 'transparent',
            color: '#333',
            padding: '0',
            margin: '0',
            cursor: 'default',
            gap: '8px',
            fontWeight: '600'
          }}
        />
      </div>
    </div>
  );
});

export default InfiniteScrollGallery;
